// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportDesignerDataTree.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import { observable, computed, action, toJS } from 'mobx';
import constants from 'logic/data/Izenda.ReportDesignerConstants';
import ReportDesignerQuerySourceDetail from 'logic/models/Izenda.ReportDesignerQuerySourceDetail';
import tracker from 'share/tracking/Izenda.Tracker';
import {
  concat,
  isNil,
  flow,
  flatMap,
  filter,
  map,
  get,
  toLower,
  escapeRegExp,
  forEach,
  isEmpty,
  find,
  some,
  remove,
  negate,
  getOr
} from 'lodash/fp';
import { parseToTree } from 'share/utils/Izenda.LazyLoadingUtils';

/**
 * DataSource Tree Model
 */
@tracker
export default class ReportDesignerDataTree {
  @observable details = [];
  @observable filterText = '';

  // TODO: BE will check valid field in the future, then remove this variable
  @observable loadedDataSource = false; // using for checking data source before render in form
  @observable relationItemIds = [];

  // TODO: Remove when BE validate valid field
  @observable isLastPage = false;
  @observable displayData = [];

  // This property will be observe because left menu need to know user has select any fields or not
  // to disable others tabs
  @observable selectedTree = [];

  // flat array of TreeNodes that the user has either selected or unselected
  @observable selectedItemNodes = [];

  // used for advanced mode grid
  selectedCategories = [];

  @computed
  get hasLoadDataSource(): any {
    return this.loadedDataSource;
  }

  @computed
  get filterSource(): any {
    const filterText = toLower(this.filterText);
    const regx = new RegExp(escapeRegExp(filterText), 'ig');
    const relationItemIds = this.relationItemIds;

    forEach(category => {
      category.querySource &&
        forEach(dataObject => {
          // filter fields
          const filterFields = filter(field => toLower(field.name).indexOf(filterText) > -1, dataObject.fields);
          dataObject.filterFields = filterFields;
        }, category.querySource);

      // filter data object
      const filterQuerySource =
        category.querySource &&
        filter(dataObject => {
          return (
            ((dataObject.filterFields && dataObject.filterFields.length) || dataObject.name.search(regx) > -1) &&
            // filter relation querySources for Simple mode
            (relationItemIds.indexOf(dataObject.id) > -1 || relationItemIds.length === 0)
          );
        }, category.querySource);

      category.filterQuerySource = filterQuerySource;
    }, this.details);

    return filter(category => category.filterQuerySource && category.filterQuerySource.length > 0, this.details);
  }

  @computed
  get showAllowNull(): boolean {
    let countSelected = 0;

    forEach(category => {
      countSelected = countSelected + filter('selected', category.querySource).length;
      // early break to prevent
      if (countSelected > 1) return false;
    }, this.details);

    return countSelected > 1;
  }

  @computed
  get selectedItems(): any {
    return this.getSelectedItems(this.details);
  }

  @computed
  get isAllDataOjectUncategory(): boolean {
    const itemHasCategory = filter(item => item.id !== constants.EMPTY_GUID, this.details);
    return (isEmpty(this.details) || !isEmpty(itemHasCategory)) ? false : true;
  }

  @computed
  get getItemIsOnlyOne(): any {
    return this.selectedItems.length === 1 ? this.selectedItems[0] : null;
  }

  @computed
  get parameterFieldIds(): any {
    return flow(
      flatMap(item => toJS(item.querySource)),
      filter('selected'),
      flatMap(querySource => toJS(querySource.fields)),
      filter('isParameter'),
      filter('filterable'),
      filter('visible'),
      filter({ inaccessible: false }),
      map(get('id'))
    )(this.details);
  }

  @computed
  get noCategory(): boolean {
    return this.details.length === 1;
  }

  @computed
  get haveDataObjects(): boolean {
    return !isEmpty(this.selectedItems);
  }

  @computed
  get calculatedFields(): any {
    const calculatedFieldQuerySource = find({ name: constants.CALCULATED_FIELD_NAME }, this.details);
    if (calculatedFieldQuerySource) {
      return calculatedFieldQuerySource.querySource[0].fields;
    }

    return [];
  }

  @computed
  get flattenQuerySourceIncludeCalculatedField() {

    let result = [];
    forEach(item => {
      forEach(childItem => {
        result = concat(result, childItem.flattenQuerySource)
      }, item.querySource);
    }, this.details);

    return result;
  }

  /**
   * Check Dirty
   */
  checkDirty(): boolean {
    return (
      this.isDirty ||
      flow(flatMap(x => (x.querySource ? x.querySource.slice() : [])), some(['isDirty', true]))(this.details)
    );
  }

  /**
   * Clean Dirty
   */
  @action
  cleanDirty(): void {
    this.resetDirty();

    forEach(item => {
      item.resetDirty && item.resetDirty();
    }, this.details);
  }

  @action
  cleanCalculatedFieldDirty(): void {
    const calculatedFieldQuerySource = find({ name: constants.CALCULATED_FIELD_NAME }, this.details);

    if (!isNil(calculatedFieldQuerySource)) {
      const querySouces = getOr([],'querySource', calculatedFieldQuerySource);
      forEach(querySouce => {
        querySouce.resetDirty && querySouce.resetDirty();
      }, querySouces);
    }
  }

  /**
   * Get list of data tree item
   * @param {Object} dataObject
   * @returns {type}
   */
  @action
  getDataTreeItems(dataObject: any): any {
    const result = [];
    const uncategorizedText = constants.UNCATEGORIZED;

    this.selectedCategories = [];
    forEach(item => {
      forEach(querySource => {
        if (querySource.isVirtualNode) {
          return;
        }

        result.push({
          querySourceId: querySource.id,
          selected: querySource.selected,
          physicalChange: dataObject && querySource.id === dataObject.id ? 2 : 0,
          state: dataObject && dataObject.selected ? constants.OBJECT_STATE.INSERT : constants.OBJECT_STATE.DELETE
        });

        // just add selected querySource or categorized category to selectedCategories
        if (!querySource.selected) {
          return;
        }

        const key = item.id || constants.EMPTY_GUID;
        if (isNil(this.selectedCategories[key])) {
          this.selectedCategories[key] = {
            name: isNil(item.name) ? uncategorizedText : item.name,
            dataObjects: [querySource]
          };
        } else {
          this.selectedCategories[key].dataObjects.push(querySource);
        }
      }, item.querySource);
    }, this.selectedTree);

    return result;
  }

  /**
   * Show data objects which have relationship with selected objects
   * @param {any} relationItems
   */
  @action
  showRelationship(relationItems: any): void {
    if (!isEmpty(relationItems)) {
      this.relationItemIds = map(get('querySourceId'), relationItems);
    }
  }

  /**
   * Get Icon
   * @param {any} objectName
   * @returns {string}
   */
  getIcon(objectName: any): string {
    return getOr('', objectName, constants.ICON);
  }

  /**
   * Read data source
   */
  @action
  readDataSource(result: any): any {
    let details = this.details;
    forEach(category => {
      if (category.isVirtualNode) {
        return;
      }
      category.querySource = map(detail => {
        detail.fields = filter({ inaccessible: false }, detail.fields);
        if (detail.isVirtualNode) {
          return detail;
        }
        return new ReportDesignerQuerySourceDetail(detail);
      }, category.querySource);
    }, result);

    if (result && !result.isFromDataSource ){
      details.replace(result);
    }

    // TODO: Remove when BE validate valid field
    this.loadedDataSource = true;
  }

  /**
   * Convert raw data to ReportDesignerQuerySourceDetail
   * @param  {Array} result
   * @return {Array}
   */
  convertDataSource(result: any): any {
    forEach(category => {
      category.expand = true;
      category.querySource = map(detail => {
        detail.expand = !!this.filterText;
        detail.fields = filter({ inaccessible: false }, detail.fields);
        return new ReportDesignerQuerySourceDetail(detail);
      }, category.querySource);
    }, result);

    return result;
  }

  /**
   * Read calculated data source
   */
  @action
  readCalculatedDataSource(result: any, isDirty: boolean): any {
    result.querySource = map(detail => {
      const querySourceDetail = new ReportDesignerQuerySourceDetail(detail);
      isDirty && querySourceDetail.markDirty();
      return querySourceDetail;
    }, result.querySource);

    this.details.length >= 1 && (this.details[1] = result);
  }

  /**
   * Delete calculated field
   */
  @action
  deleteCalculatedField(field: any): void {
    const calculatedField = this.details[1] || {};
    const calculatedFieldQuerySource = calculatedField.querySource[0];
    let fields = calculatedFieldQuerySource.fields;

    fields = remove(fieldDetail => fieldDetail.id === field.id, fields);

    if (isEmpty(fields)) {
      this.details.splice(1, 1);
    } else {
      this.details[1].querySource[0].fields = fields;
    }
  }

  /**
   * @method updateSelectedItemNodes
   * @description update SelectedItemNodes based on updates to display data
   */
  @action
  updateSelectedItemNodes(isAdd, node) {
      this.selectedItemNodes = [];
      this.selectedTree.forEach(node => {
        node.querySource.forEach(source => {
          if (source.selected) {
            this.selectedItemNodes.push(source)
          }
        })
      });
      this.selectedItemNodes = parseToTree(this.selectedItemNodes, constants.SELECTED_DATA_SOURCE_TREE_MAP);
  }

  /**
   * Check if selected data sources have a dynamic store
   */
  hasDynamicStore(): void {
    return flow(
      flatMap(category => (category.querySource ? category.querySource.slice() : [])),
      filter('isDynamic'),
      negate(isEmpty)
    )(this.selectedTree);
  }

  /**
   * Get Selected Items
   */
  getSelectedItems(dataTreeData: any): any {
    const result = [];
    forEach(item => {
      forEach(querySource => {
        if (querySource.selected) {
          const primaryFields = filter(field => {
            const ext = !isEmpty(field.extendedProperties) ? JSON.parse(field.extendedProperties) : {};
            return !isEmpty(ext) && ext.PrimaryKey;
          }, querySource.fields);

          result.push({
            aliasId: querySource.aliasId,
            querySourceId: querySource.id,
            querySourceName: querySource.name,
            selected: true,
            categoryId: item.id,
            primaryFields: primaryFields
          });
        }
      }, item.querySource);
    }, dataTreeData);
    return result;
  }

  /**
   * Get query sources from selected tree
   */
  getSelectedQuerySources(): any {
    return flow(flatMap(category => category.querySource.slice()), filter('selected'))(this.selectedTree);
  }

  /**
   * Check is another calculated field used this calculated field
   */
  isAnotherCalculatedFieldUse(field: any): boolean {
    return some(
      calculatedField =>
        calculatedField.id !== field.id && calculatedField.expression.indexOf('[' + field.name + ']') > -1,
      this.calculatedFields
    );
  }

  /**
   * Update report calculated field id when save as report
   * @param {any} calculatedFields
   */
  @action
  populateDataForCalculatedFields(calculatedFields: any): void {
    forEach(calculatedField => {
      const item = find(
        ({ name }) => name === calculatedField.name,
        this.calculatedFields
      );

      if (item) {
        item.id = calculatedField.id;
      }
    }, calculatedFields);

    this.cleanCalculatedFieldDirty();
  }
}



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.ReportDesignerDataTree.js